/**
 * SPA 应用级 QueryClient 单例。
 *
 * 不在 queryFn 中消费 React Query 的 AbortSignal：StrictMode 开发态双挂载会卸载观察者并
 * abort 首轮请求，Network 出现成对 `(canceled)`→`200`（生产无此现象）。不消费 signal 时
 * 请求继续写入 cache，重挂可复用 in-flight。离开路由时 in-flight 可能跑完再入 cache（无
 * 观察者不展示）。client 仍支持显式 `options.signal` 与超时 abort。
 */

import { MutationCache, QueryClient } from "@tanstack/react-query";

declare module "@tanstack/react-query" {
  interface Register {
    mutationMeta: {
      suppressGlobalError?: boolean;
    };
  }
}

const mutationErrorToastRef: { current: (error: unknown) => void } = {
  current: () => {},
};

/** 由 GlobalRequestErrorListener 注入 Toast 处理器。 */
export function setAppMutationErrorToastHandler(
  handler: (error: unknown) => void,
) {
  mutationErrorToastRef.current = handler;
}

export function createAppQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
        retry: 1,
      },
    },
    mutationCache: new MutationCache({
      onError: (error, _variables, _context, mutation) => {
        if (mutation.meta?.suppressGlobalError) return;
        mutationErrorToastRef.current(error);
      },
    }),
  });
}

let appQueryClient: QueryClient | null = null;

/** 浏览器 SPA 共用一个 QueryClient，避免 Provider 重建清空缓存。 */
export function getAppQueryClient() {
  if (!appQueryClient) {
    appQueryClient = createAppQueryClient();
  }
  return appQueryClient;
}

/** @deprecated 使用 createAppQueryClient；保留别名兼容既有 import。 */
export const createQueryClient = createAppQueryClient;
